// react
import { useCallback, useEffect, useState } from 'react';

// types
import { WorkPlanPayload, WorkPlan } from '../@types';

// services
import {
	createWorkPlan,
	deactivateWorkPlan as deactivateWorkPlanService,
	deleteWorkPlan as deleteWorkPlanService,
	fetchWorkPlan,
	fetchWorkPlans,
	updateWorkPlan,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface useWorkPlansParams {
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface useWorkPlansReturn {
	create: (payload: WorkPlanPayload) => Promise<ApiResponse<WorkPlan>>;
	data: WorkPlan[];
	deactivate: (id: number) => Promise<ApiResponse<WorkPlan>>;
	delete: (id: number) => Promise<ApiResponse<{ deleted: true }>>;
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<WorkPlan[]>['pagination'];
	refetch: () => Promise<void>;
	update: (id: number, payload: Partial<WorkPlanPayload>) => Promise<ApiResponse<WorkPlan>>;
}

// Hook

export const useWorkPlans = ({
	isEnabled = true,
	projectId,
	query = {},
}: useWorkPlansParams = {}): useWorkPlansReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<WorkPlan[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState<ApiResponse<WorkPlan[]>['pagination']>(undefined);

	// fetch data
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchWorkPlans({ accessToken, idToken }, { ...query, project: projectId });

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch work plans');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create work plan
	const create = useCallback(
		async (payload: WorkPlanPayload): Promise<ApiResponse<WorkPlan>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await createWorkPlan({ accessToken, idToken }, payload);

				if (!res.error) {
					// Refetch to get updated list
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to create work plan';
				return { error: { id: 'CREATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// update work plan
	const update = useCallback(
		async (id: number, payload: Partial<WorkPlanPayload>): Promise<ApiResponse<WorkPlan>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await updateWorkPlan({ accessToken, idToken }, id, payload);

				if (!res.error) {
					// Refetch to get updated list
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update work plan';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// deactivate work plan (soft-delete via status='DEACTIVATED')
	const deactivateWorkPlan = useCallback(
		async (id: number): Promise<ApiResponse<WorkPlan>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deactivateWorkPlanService({ accessToken, idToken }, id);

				if (!res.error) {
					// Refetch to get updated list
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to deactivate work plan';
				return { error: { id: 'DEACTIVATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// hard-delete work plan (DRAFT + unlinked only, enforced by backend guard)
	const deleteWorkPlan = useCallback(
		async (id: number): Promise<ApiResponse<{ deleted: true }>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deleteWorkPlanService({ accessToken, idToken }, id);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to delete work plan';
				return { error: { id: 'DELETE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	return {
		create,
		data,
		deactivate: deactivateWorkPlan,
		delete: deleteWorkPlan,
		error,
		loading,
		pagination,
		refetch: fetchData,
		update,
	};
};

// Single Work Plan Hook

export interface UseWorkPlanParams {
	id: number;
	isEnabled?: boolean;
	query?: QueryParams;
}

export interface UseWorkPlanReturn {
	data: WorkPlan | null;
	error: string | null;
	loading: boolean;
	refetch: () => Promise<void>;
	update: (payload: Partial<WorkPlanPayload>) => Promise<ApiResponse<WorkPlan>>;
}

export const useWorkPlan = ({
	id,
	isEnabled = true,
	query = {},
}: UseWorkPlanParams): UseWorkPlanReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<WorkPlan | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	// fetch data
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchWorkPlan({ accessToken, idToken }, id, query);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch work plan');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, id, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	// update work plan
	const update = useCallback(
		async (payload: Partial<WorkPlanPayload>) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await updateWorkPlan({ accessToken, idToken }, id, payload);

			if (!res.error) {
				// Refetch to get updated data
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, id, fetchData]
	);

	return {
		data,
		error,
		loading,
		refetch: fetchData,
		update,
	};
};
